import { apiClient } from "./apiClient";

export interface EntregaResumo {
  totalEntregasFinalizadas: number;
  totalEntregasExternasFinalizadas?: number;
  quantidadeEntregadores: number;
  totalEntregasEmAndamento: number;
  tempoTotalSegundos: number;
  tempoMedio: string;
  quantidadeTotalEnderecos: number;
  usuarioMenorTempo: string;
}

export interface UsuarioEntrega {
  usuarioId: number;
  nome: string;
  roleDescricao: string;
  quantidadeEntregas: number;
  tempoTotal: number;
  tempoMedio: string;
}

export interface EntregaUsuariosResponse {
  usuarios: UsuarioEntrega[];
}

export interface EntregaResumoFilters {
  dataInicio: string;
  dataFim: string;
  lojaId?: number;
}

export interface UsuarioRankingEntrega {
  usuarioId: number;
  nome: string;
  quantidadeEntregas: number;
  tempoMedio: string;
}

export interface RankingEntregaResponse {
  ranking: UsuarioRankingEntrega[];
}

export interface RankingEntregaFilters {
  dataInicio: string;
  dataFim: string;
  lojaId?: number | null;
  usuarioId?: number | null;
}

export interface RankingDiaEntregaFilters {
  lojaId?: number | null;
}

export interface FilaAguardando {
  id: number;
  entregadorId: number;
  nome: string;
  dataEntrada: string | Date;
}

export interface RankingDiaEntregaResponse {
  quantidadeTotal: number;
  quantidadeEmAndamento: number;
  entregadoresAguardando: number;
  tempoMedioGeral: string;
  ranking: UsuarioRankingEntrega[];
  aguardando: FilaAguardando[];
}

export interface PedidoExternoDetalhe {
  displayId: number;
  quantidadePizzas?: number;
}

export interface Entrega {
  id: number;
  dataInicio: string;
  dataFim: string | null;
  tempoTotal: number | null;
  status: string;
  quantidadeEnderecos: number;
  pedidosDisplayIds?: number[];
  entregaExterna?: boolean;
  pedidosExternosDetalhes?: PedidoExternoDetalhe[];
  loja: {
    id: number;
    nome: string;
  };
  entregador: {
    id: number;
    nome: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface EntregaFilters {
  dataInicio?: string;
  dataFim?: string;
  lojaId?: number | null;
  entregadorId?: number | null;
  status?: string;
  /** Número de exibição do pedido (busca em pedidos_display_ids) */
  pedidoDisplayId?: number;
  page?: number;
  limit?: number;
}

export interface PaginatedEntregaResponse {
  data: Entrega[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** Payload enxuto de GET /entregas/ativas-operacao (tela entregadores). */
export interface EntregaAtivaOperacao {
  id: number;
  entregadorId: number;
  quantidadeEnderecos: number;
  status: string;
  dataInicio: string;
  dataFim: string | null;
  entregador: {
    id: number;
    nome: string;
  };
}

export const entregaService = {
  getAll: async (
    filters: EntregaFilters
  ): Promise<PaginatedEntregaResponse> => {
    const params = new URLSearchParams();
    if (filters.dataInicio) {
      params.append("dataInicio", filters.dataInicio);
    }
    if (filters.dataFim) {
      params.append("dataFim", filters.dataFim);
    }
    if (filters.lojaId !== null && filters.lojaId !== undefined) {
      params.append("lojaId", filters.lojaId.toString());
    }
    if (filters.entregadorId !== null && filters.entregadorId !== undefined) {
      params.append("entregadorId", filters.entregadorId.toString());
    }
    if (filters.status) {
      params.append("status", filters.status);
    }
    if (
      filters.pedidoDisplayId !== null &&
      filters.pedidoDisplayId !== undefined
    ) {
      params.append("pedidoDisplayId", filters.pedidoDisplayId.toString());
    }
    if (filters.page) {
      params.append("page", filters.page.toString());
    }
    if (filters.limit) {
      params.append("limit", filters.limit.toString());
    }

    return apiClient.get(`/entregas?${params.toString()}`);
  },

  getAtivasOperacao: async (
    lojaId?: number
  ): Promise<EntregaAtivaOperacao[]> => {
    const params = new URLSearchParams();
    if (lojaId != null) {
      params.append("lojaId", lojaId.toString());
    }
    const qs = params.toString();
    return apiClient.get(`/entregas/ativas-operacao${qs ? `?${qs}` : ""}`);
  },

  getResumo: async (filters: EntregaResumoFilters): Promise<EntregaResumo> => {
    const params = new URLSearchParams();
    params.append("dataInicio", filters.dataInicio);
    params.append("dataFim", filters.dataFim);
    if (filters.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }

    return apiClient.get(`/entregas/resumo?${params.toString()}`);
  },

  getUsuarios: async (
    filters: EntregaResumoFilters
  ): Promise<EntregaUsuariosResponse> => {
    const params = new URLSearchParams();
    params.append("dataInicio", filters.dataInicio);
    params.append("dataFim", filters.dataFim);
    if (filters.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }

    return apiClient.get(`/entregas/periodo?${params.toString()}`);
  },

  getRanking: async (
    filters: RankingEntregaFilters
  ): Promise<RankingEntregaResponse> => {
    const params = new URLSearchParams();
    params.append("dataInicio", filters.dataInicio);
    params.append("dataFim", filters.dataFim);
    if (filters.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }
    if (filters.usuarioId) {
      params.append("usuarioId", filters.usuarioId.toString());
    }

    return apiClient.get(`/entregas/ranking?${params.toString()}`);
  },

  getRankingDia: async (
    filters?: RankingDiaEntregaFilters
  ): Promise<RankingDiaEntregaResponse> => {
    const params = new URLSearchParams();
    if (filters?.lojaId) {
      params.append("lojaId", filters.lojaId.toString());
    }
    const qs = params.toString();
    return apiClient.get(`/entregas/ranking-dia${qs ? `?${qs}` : ""}`);
  },

  create: async (data: {
    dataInicio: string;
    lojaId: number;
    entregadorId: number;
    quantidadeEnderecos?: number;
    pedidosDisplayIds?: number[];
    entregaExterna?: boolean;
    pedidosExternosDetalhes?: PedidoExternoDetalhe[];
  }): Promise<Entrega> => {
    return apiClient.post("/entregas", data);
  },

  update: async (
    id: number,
    data: { pedidosDisplayIds?: number[]; quantidadeEnderecos?: number }
  ): Promise<Entrega> => {
    return apiClient.put(`/entregas/${id}`, data);
  },

  finalizar: async (id: number): Promise<Entrega> => {
    return apiClient.put(`/entregas/${id}/finalizar`, {});
  },

  delete: async (id: number): Promise<void> => {
    return apiClient.delete(`/entregas/${id}`);
  },
};
